import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box, Flex, Heading, Image, Text } from '@resideo/blueprint-react';
import styled from 'styled-components';
import { resideoBlackLogo } from '../../assets/images';
import { useTranslation } from 'react-i18next';
import SeparatorLine from '../common/SeparatorLine';
import { convertMoneyAmount } from '../../routes/ServicesAndSubscriptions/Cart';
import { graphql } from 'react-relay/hooks';
import { useParams } from 'react-router';
import { fetchQuery } from 'relay-runtime';
import { useRelayEnvironment } from 'relay-hooks';
import moment from 'moment';
import PageLoading from 'components/common/PageLoading';
import { CompleteOrderQuery } from './__generated__/CompleteOrderQuery.graphql';
import { subscriptionOrderProducts } from 'data/SelectPlanData';

const PrintWrapper = styled(Flex)`
  cursor: pointer;
  font-weight: bold;
  display: block;
  @media print {
    display: block;
  }
`;

const BillBorderBox = styled(Flex)`
  border: 2px solid ${({ theme }) => theme.colors.back};
`;

const WordBreakText = styled(Text)`
  word-break: break-all;
`;

const Order: FC<any> = ({ viewerData }) => {
  const params = useParams() as any;
  const orderId = params?.id;
  const [isLoading, setLoading] = useState(true);
  const [order, setOrder] = useState<any>(null);
  const environment = useRelayEnvironment();
  const { t } = useTranslation();

  const today = moment(order?.createdAt).format('MMM DD, YYYY');

  const invoiceData = useCallback(() => {
    if (orderId !== '' && orderId !== undefined) {
      fetchQuery<CompleteOrderQuery>(
        environment,
        graphql`
          query CompleteOrderQuery($orderId: ID!) {
            order: node(id: $orderId) {
              ... on DigitalRiverOrder {
                billTo {
                  address {
                    addressLine1
                    addressLine2
                    city
                    contactName
                    countryCode
                    mailingAddressName
                    stateProvinceRegionCode
                    zipPostalCode
                  }
                  email
                  firstName
                  lastName
                  organization
                }
                createdAt
                currency
                id
                invoiceNumber
                lineItems {
                  sku
                  quantity
                  amount {
                    amount
                    currencyCode
                  }
                  billingPeriodStartDate
                  billingPeriodEndDate
                  deviceNumber
                }
                shipTo {
                  address {
                    addressLine1
                    addressLine2
                    city
                    contactName
                    countryCode
                    mailingAddressName
                    stateProvinceRegionCode
                    zipPostalCode
                  }
                  email
                  firstName
                  lastName
                  organization
                }
                paymentSource {
                  paymentSessionId
                  clientSecret
                  state
                  currency
                  type
                  currency
                  creditCard {
                    brand
                    expirationMonth
                    expirationYear
                    lastFourDigits
                  }
                }

                subTotal {
                  amount
                  currencyCode
                }
                tax {
                  amount
                  currencyCode
                }
                taxRate
                total {
                  amount
                  currencyCode
                }
                totalShipping {
                  amount
                  currencyCode
                }
              }
            }
          }
        `,
        {
          orderId,
        }
      ).subscribe({
        next: data => {
          const theOrder = {
            ...data?.order,
            lineItems: data?.order?.lineItems?.map(ele => {
              // convert amount to 'cents', then use Math.round and toFixed to avoid rounding errors
              const centsAmount = ele?.amount?.amount
                ? Math.round(parseFloat(`${ele.amount.amount}`) * 100)
                : 0;
              const qty = ele?.quantity ?? 1;
              const price = (Math.round(centsAmount / qty) / 100).toFixed(2);
              return {
                ...ele,
                ...(subscriptionOrderProducts.find(
                  item => item.sku === ele?.sku
                ) ?? {
                  name: 'Product Not Found',
                  type: 'Unknown',
                }),
                price,
              };
            }),
          };
          setOrder(theOrder);
          setLoading(false);
        },
        error: () => {
          setOrder({});
          setLoading(false);
        },
      });
    } else {
      setLoading(false);
      setOrder({});
    }
  }, [environment, orderId]);

  useEffect(() => {
    invoiceData();
  }, [orderId]);

  const hasProduct =
    order?.lineItems?.filter(f => f.type === 'product').length > 0;

  const taxData = JSON.parse(
    `${localStorage.getItem('taxExemptionApplication')}`
  );

  return isLoading || order === null ? (
    <PageLoading />
  ) : (
    <PrintWrapper
      width={'100%'}
      marginBottom='xxxLarge'
      padding={['1.25rem', '']}>
      <Box paddingBottom={'large'}>
        <Image alt='resideo' src={resideoBlackLogo} />
      </Box>
      <br />
      <Flex>
        <Box display='flex'>
          <Heading width={'9rem'} fontWeight='700'>
            {t('cart.completeOrder.orderNumber')}
          </Heading>
          <WordBreakText fontWeight={400}>{order?.invoiceNumber}</WordBreakText>
        </Box>
      </Flex>
      <Flex paddingY={'small'}>
        <Box display='flex'>
          <Heading width={'9rem'} fontWeight='700'>
            {t('cart.completeOrder.date')}
          </Heading>
          <WordBreakText fontWeight={400}>{today}</WordBreakText>
        </Box>
      </Flex>
      <Flex marginBottom='xLarge'>
        <Box display='flex'>
          <Heading width={'9rem'} fontWeight='700'>
            {t('cart.completeOrder.orderStatus')}
          </Heading>
          <WordBreakText fontWeight={400}>
            {t('cart.completeOrder.completed')}
          </WordBreakText>
        </Box>
      </Flex>
      <BillBorderBox
        marginBottom={'large'}
        width={'100%'}
        display={'flex'}
        flexDirection={['column', 'row']}
        justifyContent={'space-between'}
        paddingY={'large'}
        paddingX={'medium'}>
        <Box display='flex'>
          <Flex
            flexDirection='column'
            marginBottom={['large', 'large', '', '']}>
            <Heading paddingBottom={'small'}>
              {t('cart.completeOrder.contactInformation')}
            </Heading>
            <WordBreakText fontWeight={400}>
              {viewerData?.contactEmail}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {viewerData?.primaryPhoneNumber ??
                viewerData?.phoneNumbers?.[0].number}
            </WordBreakText>
          </Flex>
        </Box>
        <Box display='flex' paddingLeft={['', 'medium']}>
          <Flex
            flexDirection='column'
            marginBottom={['large', 'large', '', '']}>
            <Heading paddingBottom={'small'}>
              {t('cart.completeOrder.billingAddress')}
            </Heading>
            <WordBreakText fontWeight={400}>
              {order?.billTo?.organization}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {order?.billTo?.address?.contactName}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {order?.billTo?.address?.addressLine1}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {order?.billTo?.address?.stateProvinceRegionCode}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {order?.billTo?.address?.zipPostalCode}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {order?.billTo?.address?.countryCode}
            </WordBreakText>
          </Flex>
        </Box>
        <Box display='flex' paddingLeft={['', 'medium']}>
          <Flex
            flexDirection='column'
            marginBottom={['large', 'large', '', '']}>
            <Heading paddingBottom={'small'}>
              {t('cart.completeOrder.paymentMethod')}
            </Heading>
            {order.paymentSource?.type === 'PAYPAL' ? (
              <WordBreakText fontWeight={400}>PayPal</WordBreakText>
            ) : (
              <>
                <WordBreakText fontWeight={400}>
                  {order?.paymentSource?.creditCard?.brand}
                </WordBreakText>
                <WordBreakText fontWeight={400}>
                  **** **** ****{' '}
                  {order?.paymentSource?.creditCard?.lastFourDigits}
                </WordBreakText>
              </>
            )}
          </Flex>
        </Box>
        <Box display='flex' paddingLeft={['', 'medium']}>
          <Flex
            flexDirection='column'
            marginBottom={['large', 'large', '', '']}>
            <Heading paddingBottom={'small'}>
              {t('cart.completeOrder.taxExemptionInformation')}
            </Heading>
            <WordBreakText fontWeight={400}>
              {taxData?.companyName}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {taxData?.taxAuthority}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {t('cart.billing.exemptionStartDateLable')}{' '}
              {localStorage.getItem('temp-tax-start-date')
                ? localStorage.getItem('temp-tax-start-date')
                : 'N/A'}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {t('cart.billing.exemptionEndDateLable')}{' '}
              {localStorage.getItem('temp-tax-end-date')
                ? localStorage.getItem('temp-tax-end-date')
                : 'N/A'}
            </WordBreakText>
            <WordBreakText fontWeight={400}>
              {t('cart.billing.certFileName')}{' '}
              {localStorage.getItem('temp-file-name')
                ? localStorage.getItem('temp-file-name')
                : 'N/A'}
            </WordBreakText>
          </Flex>
        </Box>
      </BillBorderBox>
      {order?.lineItems?.map((item, i) => (
        <Flex
          key={i}
          paddingTop='medium'
          width={'100%'}
          display={'flex'}
          flexDirection={['column', 'row']}
          justifyContent={'space-between'}
          alignItems={'center'}>
          <Box
            display='flex'
            width={'17rem'}
            minWidth={'17rem'}
            maxWidth={'17rem'}>
            <Flex
              flexDirection='column'
              width={1}
              justifyContent='space-between'
              marginBottom={['large', 'large', '', '']}>
              <Heading color={'#221E1F'}>{item.name}</Heading>
              {item.type === 'HAB' && (
                <Heading color={'#221E1F'} letterSpacing='0'>
                  {t('cart.houseHoldTaxes')}
                </Heading>
              )}
              {item.type === 'subscription' && (
                <Heading color={'#221E1F'}>
                  {t('cart.completeOrder.perMonthPrice')}{' '}
                  {t('cart.completeOrder.perMonth')}
                </Heading>
              )}
              {item.type === 'AirCycle_device_subscription' && (
                <Heading color={'#221E1F'}>
                  {t('cart.deviceId')}: {item.deviceNumber}
                </Heading>
              )}
              <WordBreakText
                fontSize={'0.875rem'}
                fontWeight={500}
                color={'gray'}
                paddingY={'small'}>
                {item.sku}
              </WordBreakText>
              {item.type === 'HAB' && (
                <Text
                  data-test-cart-item-sku
                  color='#221E1F'
                  fontSize='0.875rem'
                  fontWeight='400'
                  paddingTop='xSmall'>
                  {t('cart.activationServiceLabel')}
                </Text>
              )}
            </Flex>
          </Box>
          <Flex
            flexDirection={['column', 'row']}
            width={1}
            justifyContent='space-between'
            marginBottom={['large', 'large', '', '']}>
            <Heading color={'#221E1F'}>
              {t('cart.completeOrder.completed')}
            </Heading>
            <Heading color={'#221E1F'}>
              {t('cart.completeOrder.quantityPurchased')}
            </Heading>
            <Heading color={'#221E1F'}>x{item.quantity || 1}</Heading>
          </Flex>
          <Box
            justifyContent={'flex-end'}
            display='flex'
            width={'7rem'}
            maxWidth={'7rem'}
            minWidth={'7rem'}>
            <Flex
              flexDirection='column'
              marginBottom={['large', 'large', '', '']}>
              <Heading color={'#221E1F'}>
                {convertMoneyAmount(
                  {
                    amount: item.amount?.amount,
                    currencyCode: item.amount?.currencyCode,
                  },
                  'en-us'
                )}
              </Heading>
            </Flex>
          </Box>
        </Flex>
      ))}
      <SeparatorLine borderColor={'#000'} />
      <Box>
        {hasProduct && (
          <Box
            display='flex'
            width='100%'
            justifyContent='flex-end'
            paddingBottom='small'>
            <WordBreakText
              fontWeight={400}
              width='calc(100% - 7.625rem)'
              textAlign='right'
              color='textHeading'>
              {t('cart.shipping.shipping')}
            </WordBreakText>
            <WordBreakText
              fontWeight={400}
              width='7.625rem'
              textAlign='right'
              color='textHeading'>
              {convertMoneyAmount(
                {
                  amount: order?.totalShipping?.amount,
                  currencyCode: order?.totalShipping?.currencyCode,
                },
                'en-us'
              )}
            </WordBreakText>
          </Box>
        )}
        <Box
          display='flex'
          width='100%'
          justifyContent='flex-end'
          paddingBottom='small'>
          <WordBreakText
            fontWeight={400}
            width='calc(100% - 7.625rem)'
            textAlign='right'
            color='textHeading'>
            {t('tax')}
          </WordBreakText>
          <WordBreakText
            fontWeight={400}
            width='7.625rem'
            textAlign='right'
            color='textHeading'>
            {convertMoneyAmount(
              {
                amount: order?.tax?.amount,
                currencyCode: order?.tax?.currencyCode,
              },
              'en-us'
            )}
          </WordBreakText>
        </Box>
      </Box>
      <Box>
        <Box
          display='flex'
          alignItems='center'
          width='100%'
          justifyContent='flex-end'>
          <Heading
            fontSize='medium'
            fontWeight='bold'
            letterSpacing='normal'
            color='textHeading'
            width='calc(100% - 7.625rem)'
            textAlign='right'>
            {t('totalToday')}
          </Heading>
          <Heading
            fontSize='medium'
            fontWeight='bold'
            letterSpacing='normal'
            color='textHeading'
            width='7.625rem'
            textAlign='right'>
            {convertMoneyAmount(
              {
                amount: order?.total?.amount,
                currencyCode: order?.total?.currencyCode,
              },
              'en-us'
            )}
          </Heading>
        </Box>
      </Box>
    </PrintWrapper>
  );
};

export default Order;
